/**
 * TOTP two-factor auth API layer (AF-003b).
 *
 * Hand-typed against the BE-002.10 contract because that backend PR is not yet
 * merged, so these endpoints are absent from the OpenAPI snapshot that
 * `pnpm gen:types` reads. Once BE-002.10 lands, fold these into `schema.ts`
 * and delete the hand-typed request/response shapes below.
 *
 * Contract:
 *   POST /auth/2fa/totp/enroll  -> { provisioning_uri, secret, backup_codes[] }
 *   POST /auth/2fa/totp/verify  { code } -> 200 (enables) | 401 invalid
 *   POST /auth/2fa/totp/disable { code } -> 200 | 401
 *   POST /auth/2fa/login { challenge_token, code } -> TokenPair
 */
import { apiClient } from '@/lib/api/client';
import type { TokenPair } from '@/lib/api/types';

export interface TotpEnrollOut {
  /** otpauth:// URI to render as a QR / paste into an authenticator app. */
  provisioning_uri: string;
  /** Base32 secret for manual entry when the QR can't be scanned. */
  secret: string;
  /** One-time recovery codes — shown to the operator exactly once. */
  backup_codes: string[];
}

export interface TotpCodeIn {
  code: string;
}

export interface TotpStatusOut {
  enabled: boolean;
  backup_codes_remaining: number;
}

/** Current 2FA state for the signed-in admin (authoritative source). */
export async function getTotpStatus(): Promise<TotpStatusOut> {
  const res = await apiClient.get<TotpStatusOut>('/auth/2fa/totp/status');
  return res.data;
}

export interface TotpChallengeIn {
  challenge_token: string;
  code: string;
}

/** Begin enrollment: mints a fresh secret + backup codes (not yet enabled). */
export async function enrollTotp(): Promise<TotpEnrollOut> {
  const res = await apiClient.post<TotpEnrollOut>('/auth/2fa/totp/enroll', {});
  return res.data;
}

/** Confirm the freshly-enrolled secret with a live code — enables 2FA. */
export async function verifyTotp(code: string): Promise<void> {
  await apiClient.post('/auth/2fa/totp/verify', { code } satisfies TotpCodeIn);
}

/** Turn 2FA off — gated on a current TOTP (or backup) code. */
export async function disableTotp(code: string): Promise<void> {
  await apiClient.post('/auth/2fa/totp/disable', { code } satisfies TotpCodeIn);
}

/**
 * Complete a login challenge. `code` accepts either a 6-digit TOTP or a
 * one-time backup code. Resolves to the normal TokenPair on success.
 */
export async function loginTotp(challengeToken: string, code: string): Promise<TokenPair> {
  const res = await apiClient.post<TokenPair>('/auth/2fa/login', {
    challenge_token: challengeToken,
    code,
  } satisfies TotpChallengeIn);
  return res.data;
}
